import {
    Button as AntButton,
    DatePicker,
    Modal,
    Segmented,
    Select,
    Spin,
    Table,
    Tooltip,
} from 'antd';
import React from 'react';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import {
    Client,
    ClientCoupon,
    CouponCampaign,
    CouponSide,
    PaginatedClientCoupons,
} from '../../../stores/client';
import style from './style.module.scss';
import CircleIcon from '../../../icons/CheckCircleEmpty.svg?react';
import TicketIcon from '../../../icons/Ticket.svg?react';
import { useClientStore } from '../../../contexts/mobx';
import { observer } from 'mobx-react';
import * as Colors from '../../../styles/colors';
import { noUserSelectedError } from '../../../utils/error';
import { SearchInput } from '../../search-input';
import { TimezoneSelect } from '../../timezone-select';
import {
    convertLocalToUtcString,
    convertUtcToLocalMoment,
    convertUtcToLocalString,
    getUserTimezoneOrFallback,
    DATE_TIME_FORMAT,
} from '../../../utils/timezone';

type CouponsProps = {
    clientObject?: Client;
};

type Filters = {
    side: CouponSide;
    campaign: CouponCampaign;
    search: string;
    page: number;
    perPage: number;
};

const INITIAL_FILTERS: Filters = {
    side: 'issued',
    campaign: 'full_app',
    search: '',
    page: 1,
    perPage: 10,
};

const PROMOTION_SOLD_OUT_MESSAGE =
    'Coupon cannot be reissued (max qty. reached)';

// Inferred wording: the Figma has no Expired tooltip to copy from.
const PROMOTION_EXPIRED_MESSAGE = 'Coupon cannot be reissued (expired)';

const EDIT_UNAVAILABLE_MESSAGE =
    'Editing is not available for this coupon type';

// Independent of DATE_TIME_FORMAT on purpose: splitting the formatted
// string (e.g. on the space) would silently break if DATE_TIME_FORMAT
// ever changes shape.
const EXPIRY_DATE_FORMAT = 'YYYY-MM-DD';
const EXPIRY_TIME_FORMAT = 'HH:mm:ss';

const ACTIVATION_TIME_FORMAT = 'h:mm A';
const ACTIVATION_DATE_FORMAT = 'MMM D, YYYY';

// Display only: record.status === 'active' is also a predicate elsewhere
// in this file (reissue gating, row class), and those must keep reading
// the raw value.
function redeemedStatusLabel(status: string | null | undefined) {
    if (status === 'active') {
        return 'used';
    }
    if (status === 'soldOut') {
        return 'Sold out';
    }
    return status;
}

// Some thumbnails are a bare filename (e.g. "coupon.png") rather than a
// URL: that 404s to a broken-image glyph, so only an absolute http(s)
// URL is treated as a real thumbnail.
const isAbsoluteImageUrl = (value: string) => /^https?:\/\//i.test(value);

// Seeds from the grant's own stored timezone, not the browser's: a grant
// created by an agent in one zone can be edited by an agent in another
// without silently moving the displayed time.
const getTimezoneForRecord = (record: ClientCoupon) =>
    record.timezone || getUserTimezoneOrFallback();

const getValidMomentForEdit = (
    momentFromDict: Moment | null | undefined,
    recordExpireAt: Moment | string | null | undefined,
    timezone: string
): Moment => {
    if (momentFromDict?.isValid()) {
        return momentFromDict;
    }

    if (recordExpireAt) {
        const momentFromRecord = moment(recordExpireAt);
        if (momentFromRecord.isValid()) {
            return momentFromRecord;
        }
    }

    return moment().tz(timezone);
};

export const UserCouponsTab = observer(({ clientObject }: CouponsProps) => {
    const clientStore = useClientStore();

    // Local to this instance, not the store: the profile drawer can mount
    // more than one UserCouponsTab at once (CSS-toggled, antd keeps tab
    // panes alive), and each instance owns its own filters.
    const [
        clientCoupons,
        setClientCoupons,
    ] = React.useState<PaginatedClientCoupons | null>(null);
    const [
        isFetchingClientCoupons,
        setIsFetchingClientCoupons,
    ] = React.useState(false);
    // Guards against out-of-order responses (e.g. a page-2 request still
    // in flight when a toggle fires page-1): only the most recently
    // issued request may write.
    const latestRequestId = React.useRef(0);

    const [filters, setFilters] = React.useState<Filters>(INITIAL_FILTERS);
    const [searchDraft, setSearchDraft] = React.useState('');

    const [editingKey, setEditingKey] = React.useState<string | null>(null);
    const [newExpireAtDict, setNewExpireAtDict] = React.useState<{
        [key: string]: Moment | null;
    }>({});
    const [newTimezoneDict, setSelectedTimezoneDict] = React.useState<{
        [key: string]: string;
    }>({});

    const [selectedCoupon, setSelectedCoupon] = React.useState<ClientCoupon>();
    const [reissueCouponModal, setReissueCouponModal] = React.useState(false);

    // Adjusts state during render (not an effect) on purpose: an
    // effect-driven reset would commit after the fetch effect below,
    // firing it once on the stale page and again once the reset lands.
    const [resetForClientId, setResetForClientId] = React.useState(
        clientObject?.id
    );
    if (clientObject?.id !== resetForClientId) {
        setResetForClientId(clientObject?.id);
        setSearchDraft('');
        setFilters((previous) => ({ ...previous, page: 1, search: '' }));
    }

    const handleSideChange = (side: CouponSide) => {
        setSearchDraft('');
        setFilters((previous) => ({
            ...previous,
            side,
            search: '',
            page: 1,
        }));
    };

    const handleCampaignChange = (campaign: CouponCampaign) => {
        setFilters((previous) => ({ ...previous, campaign, page: 1 }));
    };

    const handleSearchSubmit = () => {
        setFilters((previous) => ({
            ...previous,
            search: searchDraft,
            page: 1,
        }));
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
        if (e.target.value === '') {
            // Clearing the box submits immediately, same as pressing enter.
            setFilters((previous) => ({ ...previous, search: '', page: 1 }));
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const handlePaginationChange = (page: number, perPage: number) => {
        setFilters((previous) => ({ ...previous, page, perPage }));
    };

    const fetchCoupons = React.useCallback(async () => {
        if (!clientObject?.id) {
            return;
        }
        const requestId = ++latestRequestId.current;
        setIsFetchingClientCoupons(true);

        const result = await clientStore.fetchClientCoupons({
            clientId: clientObject.id,
            side: filters.side,
            campaign: filters.campaign,
            page: filters.page,
            perPage: filters.perPage,
            // q is only declared on the redeemed endpoints; the issued pair
            // would silently ignore it, so it never leaves for that side.
            ...(filters.side === 'redeemed' && filters.search
                ? { q: filters.search }
                : {}),
        });

        // A superseded response touches neither rows nor spinner: clearing
        // them here would drop the Table's loading state while the newer
        // request is still in flight.
        if (requestId !== latestRequestId.current) {
            return;
        }

        setIsFetchingClientCoupons(false);
        // A failed call keeps whatever was already on screen; only
        // success replaces it.
        if (result) {
            setClientCoupons(result);
        }
    }, [clientObject?.id, filters, clientStore]);

    React.useEffect(() => {
        void fetchCoupons();
    }, [fetchCoupons]);

    const handleEdit = (record: ClientCoupon) => {
        setEditingKey(record.id.toString());

        const timezone = getTimezoneForRecord(record);
        setSelectedTimezoneDict({ [record.id]: timezone });
        const localDateString = convertUtcToLocalString(
            record.expireAt,
            timezone
        );
        setNewExpireAtDict({
            [record.id]: localDateString
                ? moment(localDateString)
                : moment(record.expireAt), // fallback just in case
        });
    };

    const handleCancelEdit = () => {
        setEditingKey(null);
    };

    const handleSaveExpireAt = async (id: string) => {
        const timezone = newTimezoneDict[id];
        if (!timezone) {
            Modal.error({
                title: 'Invalid Timezone',
                content: 'Please select a timezone before saving.',
            });
            return;
        }

        const newDate = newExpireAtDict[id];
        if (!newDate) {
            Modal.error({
                title: 'Invalid Date',
                content: 'Please select a valid date and time before saving.',
            });
            return;
        }

        const utcDateString = convertLocalToUtcString(newDate, timezone);
        if (!utcDateString) {
            Modal.error({
                title: 'Date Conversion Error',
                content: 'Failed to convert date to UTC. Please try again.',
            });
            return;
        }

        const success =
            clientObject &&
            (await clientStore.updateClientGrant(
                clientObject.id,
                Number(id),
                timezone,
                utcDateString
            ));

        if (success) {
            void fetchCoupons();
        }

        setEditingKey(null);
    };

    const handleReissueCoupon = React.useCallback(
        (coupon: ClientCoupon) => {
            if (!clientObject) {
                Modal.error({ title: noUserSelectedError });
            } else {
                setSelectedCoupon(coupon);
                setReissueCouponModal(true);
            }
        },
        [clientObject]
    );

    const renderImage = (record: ClientCoupon) =>
        record.thumbnail && isAbsoluteImageUrl(record.thumbnail) ? (
            <img alt="" src={record.thumbnail} className={style.thumbnail} />
        ) : (
            <span className={style.no_image}>No image</span>
        );

    // With paranoid: false on the API's include, a withdrawn promotion
    // still carries its title: a null title here means no translation
    // for the CMS's current language, not that it was withdrawn.
    const renderCouponDetail = (record: ClientCoupon) => (
        <div className={style.coupon_info}>
            <span className={style.coupon_title}>
                {record.title ?? 'Untranslated'}
            </span>
            <span>#{record.promotionId}</span>
        </div>
    );

    const issuedColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Image',
            key: 'thumbnail',
            render: (_: unknown, record: ClientCoupon) => renderImage(record),
        },
        {
            title: 'Coupon detail',
            key: 'title',
            render: (_: unknown, record: ClientCoupon) =>
                renderCouponDetail(record),
        },
        {
            title: 'Exp. date',
            key: 'expireAt',
            render: (_: unknown, record: ClientCoupon) => {
                const key = record.id.toString();

                if (editingKey === key) {
                    const timezone =
                        newTimezoneDict[record.id] ||
                        getTimezoneForRecord(record);
                    const expireAt = getValidMomentForEdit(
                        newExpireAtDict[record.id],
                        record.expireAt,
                        timezone
                    );

                    return (
                        <div className={style.editingContainer}>
                            <TimezoneSelect
                                value={timezone}
                                onChange={(newTz) =>
                                    setSelectedTimezoneDict({
                                        ...newTimezoneDict,
                                        [record.id]: newTz,
                                    })
                                }
                                // width: 100%, not a fixed width: this drawer
                                // is 563px, and a hard-coded control wider
                                // than its column pushed the row out of it.
                                style={{ width: '100%' }}
                            />
                            <DatePicker
                                format={DATE_TIME_FORMAT}
                                showTime={{ format: 'HH:mm' }}
                                value={expireAt}
                                onChange={(newMoment) =>
                                    setNewExpireAtDict({
                                        ...newExpireAtDict,
                                        [record.id]: newMoment,
                                    })
                                }
                                style={{ width: '100%' }}
                            />
                        </div>
                    );
                }

                const timezone = getTimezoneForRecord(record);
                const localExpireAt = convertUtcToLocalMoment(
                    record.expireAt,
                    timezone
                );
                return (
                    <div className={style.readOnlyContainer}>
                        {record.expireAt ? (
                            <>
                                <div className={style.timezoneLabel}>
                                    {timezone}
                                </div>
                                <div>
                                    {localExpireAt?.format(EXPIRY_DATE_FORMAT)}
                                </div>
                                <div>
                                    {localExpireAt?.format(EXPIRY_TIME_FORMAT)}
                                </div>
                            </>
                        ) : (
                            <span>N/A</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: ClientCoupon) => {
                const key = record.id.toString();

                if (editingKey === key) {
                    return (
                        <div>
                            <AntButton
                                onClick={() => handleSaveExpireAt(key)}
                                type="link"
                            >
                                Save
                            </AntButton>
                            <AntButton onClick={handleCancelEdit} type="link">
                                Cancel
                            </AntButton>
                        </div>
                    );
                }

                if (record.grantType === 'rpp_instant_prize') {
                    // getGrantByIdForUser excludes this grant type (PATCH
                    // throws PromotionGrantNotFound for these rows), so
                    // Edit is disabled rather than offering a dead save.
                    return (
                        <Tooltip
                            placement="top"
                            title={EDIT_UNAVAILABLE_MESSAGE}
                        >
                            <span>
                                <AntButton
                                    type="link"
                                    disabled
                                    className={style.edit_button_disabled}
                                >
                                    Edit
                                </AntButton>
                            </span>
                        </Tooltip>
                    );
                }

                return (
                    <AntButton onClick={() => handleEdit(record)} type="link">
                        Edit
                    </AntButton>
                );
            },
        },
    ];

    const redeemedColumns = [
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, record: ClientCoupon) => (
                <div
                    className={
                        record.status === 'active'
                            ? `${style.status} ${style.active_green}`
                            : record.status === 'upcoming'
                            ? `${style.status} ${style.pending_orange}`
                            : `${style.status} ${style.inactive_grey}`
                    }
                >
                    <CircleIcon
                        stroke={
                            record.status === 'active'
                                ? Colors.UNKNOWN_ACTIVE_GREEN
                                : record.status === 'upcoming'
                                ? Colors.UNKNOWN_PENDING_ORANGE
                                : Colors.UNKNOWN_TEXT_INACTIVE
                        }
                        strokeWidth="2px"
                        className={style.active_circle}
                    />
                    {redeemedStatusLabel(record.status)}
                </div>
            ),
        },
        {
            title: 'Image',
            key: 'thumbnail',
            render: (_: unknown, record: ClientCoupon) => renderImage(record),
        },
        {
            title: 'Coupon detail',
            key: 'title',
            render: (_: unknown, record: ClientCoupon) =>
                renderCouponDetail(record),
        },
        {
            title: 'Activation date',
            key: 'createdAt',
            render: (_: unknown, record: ClientCoupon) => {
                const activatedAt = moment(record.createdAt);
                return (
                    <>
                        {record.createdAt != null && (
                            <div className={style.activation}>
                                <span>
                                    {activatedAt.format(ACTIVATION_TIME_FORMAT)}
                                </span>
                                <span className={style.activation_date}>
                                    {activatedAt.format(ACTIVATION_DATE_FORMAT)}
                                </span>
                            </div>
                        )}
                    </>
                );
            },
        },
        {
            title: 'Re-issue',
            key: 'reissue',
            render: (_: unknown, record: ClientCoupon) => {
                // An already-reissued activation can never be reissued
                // again: this cell shows when instead of the reissue control.
                if (record.deletedAt != null) {
                    return (
                        <span className={style.reissued_at}>
                            {`Re-issued: ${moment(record.deletedAt).format(
                                ACTIVATION_DATE_FORMAT
                            )}`}
                        </span>
                    );
                }

                // Shown disabled rather than hidden: a promotion that sold
                // out or expired since the last fetch should tell staff why.
                if (
                    record.status === 'soldOut' ||
                    record.status === 'expired'
                ) {
                    const disabledMessage =
                        record.status === 'soldOut'
                            ? PROMOTION_SOLD_OUT_MESSAGE
                            : PROMOTION_EXPIRED_MESSAGE;
                    return (
                        <Tooltip placement="top" title={disabledMessage}>
                            <div className={style.reissue}>
                                <button
                                    className={`${style.reissue_button} ${style.reissue_button_disabled}`}
                                    disabled
                                >
                                    <TicketIcon />
                                </button>
                            </div>
                        </Tooltip>
                    );
                }

                if (record.status !== 'active') {
                    return null;
                }

                return (
                    <Tooltip placement="top" title="Reissue coupon">
                        <div className={style.reissue}>
                            <button
                                className={style.reissue_button}
                                onClick={() => handleReissueCoupon(record)}
                            >
                                <TicketIcon />
                            </button>
                        </div>
                    </Tooltip>
                );
            },
        },
    ];

    const redeemedRowClassName = (record: ClientCoupon) =>
        record.status !== 'active'
            ? record.status === 'upcoming'
                ? style.upcoming_row
                : style.expired_row
            : style.active_row;

    return (
        <>
            <div className={style.coupons_tab}>
                <div className={style.filters_container}>
                    <Segmented
                        value={filters.side}
                        onChange={(value) =>
                            handleSideChange(value as CouponSide)
                        }
                        options={[
                            { label: 'Received', value: 'issued' },
                            { label: 'Used', value: 'redeemed' },
                        ]}
                    />
                    <Select
                        aria-label="Campaign"
                        value={filters.campaign}
                        onChange={(value: CouponCampaign) =>
                            handleCampaignChange(value)
                        }
                        options={[
                            { label: 'Full app', value: 'full_app' },
                            { label: 'RPP', value: 'rpp' },
                        ]}
                    />
                    {filters.side === 'redeemed' && (
                        <div className={style.search_field}>
                            <SearchInput
                                searchValue={searchDraft}
                                onChange={handleSearchChange}
                                onKeyDown={handleSearchKeyDown}
                                onClickIcon={handleSearchSubmit}
                            />
                        </div>
                    )}
                </div>
                <Table
                    columns={
                        filters.side === 'issued'
                            ? issuedColumns
                            : redeemedColumns
                    }
                    dataSource={clientCoupons?.results ?? []}
                    rowKey="id"
                    loading={isFetchingClientCoupons}
                    rowClassName={
                        filters.side === 'redeemed'
                            ? redeemedRowClassName
                            : undefined
                    }
                    pagination={{
                        current: filters.page,
                        pageSize: filters.perPage,
                        total: clientCoupons?.total ?? 0,
                        showSizeChanger: true,
                        onChange: handlePaginationChange,
                    }}
                />
            </div>
            <Modal
                title="Reissue coupon to client"
                open={reissueCouponModal}
                onOk={async () => {
                    if (clientObject && selectedCoupon) {
                        await clientStore.reissuePromotion(
                            clientObject.id,
                            selectedCoupon.id
                        );
                    }
                    setReissueCouponModal(false);
                    void fetchCoupons();
                }}
                onCancel={() => setReissueCouponModal(false)}
            >
                {clientStore.isReissuingPromotion ? (
                    <Spin />
                ) : (
                    <>
                        <p>
                            This action will reissue the coupon "
                            {selectedCoupon?.title}" to user id #
                            {clientObject?.id}.
                        </p>
                        <h4>Are you sure you want to reissue this coupon?</h4>
                    </>
                )}
            </Modal>
        </>
    );
});
